import { useEffect, useState } from 'react';
import { Lightbulb, Loader2, PenLine, ChevronDown, Upload, X } from 'lucide-react';
import { writeAdDraft, type AdDraft } from '@generated/server/adhelper';
import { SketchPad } from '@generated/components/SketchPad';
import { GeorgeNote } from '@generated/components/GeorgeNote';
import type { Details } from '@generated/components/StepDetails';

interface Props {
  brief: string; adSize?: string; widthIn?: number | null; heightIn?: number | null; paper?: string; multiMonth: boolean;
  d: Details; onChange: (d: Details) => void;
  sketch: File | null; onSketch: (f: File | null) => void; graphics: File[]; onGraphics: (f: File[]) => void;
}

const MARK = '\n\n--- AD HELPER ---';
const area = 'mt-1 min-h-24 w-full border border-input bg-background p-3 text-sm';
const h3 = 'text-xs font-semibold uppercase tracking-wider';

export function AdHelper({ brief, adSize, widthIn, heightIn, paper, multiMonth, d, onChange, sketch, onSketch, graphics, onGraphics }: Props) {
  const [open, setOpen] = useState(false);
  const [onSite, setOnSite] = useState(false);
  const [site, setSite] = useState('');
  const [words, setWords] = useState('');
  const [idea, setIdea] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<AdDraft | null>(null);
  const before = d.notes.split(MARK)[0] ?? '';

  // Keep the helper's answers in the order notes so they reach the Orders page.
  useEffect(() => {
    const lines = [
      words.trim() && `Their words for the ad:\n${words.trim()}`,
      idea.trim() && `What they have in mind:\n${idea.trim()}`,
      onSite && `Graphics/logo: please take from their website${site.trim() ? ` — ${site.trim()}` : ''}`,
      sketch && 'Sketch attached (see Ad Files).',
    ].filter(Boolean);
    const next = lines.length ? `${before}${MARK}\n${lines.join('\n\n')}` : before;
    if (next !== d.notes) onChange({ ...d, notes: next });
  }, [words, idea, onSite, site, sketch]); // eslint-disable-line react-hooks/exhaustive-deps

  const suggest = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await writeAdDraft({ data: { brief: brief.split('\n')[0] ?? '', adSize, paper, words: [words, idea].filter(Boolean).join('\n') || 'Please suggest copy.', businessInfo: [d.businessName, d.address, d.phone, site].filter(Boolean).join(' · ') } });
      setDraft(r);
    } catch (e) { console.error(e); setErr(e instanceof Error ? e.message : 'Could not suggest copy right now.'); } finally { setBusy(false); }
  };
  const useDraft = () => {
    if (!draft) return;
    setWords([draft.headline, draft.subhead, draft.body, draft.callToAction, ...draft.details].filter(Boolean).join('\n'));
    setDraft(null);
  };

  return (
    <section className="mb-8 overflow-hidden border-2 border-[hsl(var(--brand-blue))] bg-card shadow-[6px_6px_0_0_hsl(var(--primary))]">
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex w-full items-center justify-between gap-3 bg-[hsl(var(--brand-blue))] p-5 text-left text-primary-foreground hover:opacity-95">
        <span className="flex items-center gap-2">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-foreground text-[hsl(var(--brand-blue))]"><Lightbulb className="h-6 w-6" /></span>
          <span><span className="block font-[family-name:var(--font-heading)] text-xl font-bold sm:text-2xl">Need help with your ad?</span>
            <span className="block text-sm opacity-90">Sketch your idea, tell us what to say, and we'll design it for you. Included with your ad.</span></span>
        </span>
        <span className="hidden shrink-0 bg-primary px-3 py-2 text-xs font-semibold uppercase tracking-wider sm:inline">{open ? 'Close' : 'Try it free'}</span>
        <ChevronDown className={`h-6 w-6 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="space-y-7 border-t border-border p-4">
          <div className="border-l-4 border-primary bg-muted/50 p-3 text-sm">
            <strong>How it works:</strong> send us your sketch, words and graphics. We'll design your ad and email you a proof, then work with you directly until you're happy. Help with writing and design is included in the price{multiMonth ? ', and on your multi-month contract you can change your ad month to month or season to season' : ''}.
          </div>

          <div>
            <h3 className={h3}>1. Sketch your ad</h3>
            {adSize && widthIn && heightIn
              ? <div className="mt-2 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_300px]"><div className="min-w-0"><SketchPad sizeName={adSize} widthIn={widthIn} heightIn={heightIn} saved={sketch} onSave={onSketch} /></div><GeorgeNote /></div>
              : <p className="mt-2 text-sm text-muted-foreground">Pick an ad size first to get a sketch box in its shape.</p>}
          </div>

          <div>
            <h3 className={h3}>2. Your words</h3>
            <label htmlFor="ah-words" className="mt-2 block text-sm">What should the ad say? Your name, headline, offer, dates, address, phone…</label>
            <textarea id="ah-words" value={words} onChange={(e) => setWords(e.target.value)} className={area} />
            <label htmlFor="ah-idea" className="mt-3 block text-sm">Or just tell us what you have in mind: the feeling, who it's for, what you want people to do.</label>
            <textarea id="ah-idea" value={idea} onChange={(e) => setIdea(e.target.value)} className={area} />
            <button onClick={() => void suggest()} disabled={busy} className="mt-2 inline-flex min-h-11 items-center gap-2 bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}Suggest some copy for me</button>
            {err && <p className="mt-2 text-sm text-destructive">{err}</p>}
            {draft && (
              <div className="mt-3 border-2 border-foreground bg-background p-4 text-center">
                <div className="font-[family-name:var(--font-heading)] text-xl font-extrabold">{draft.headline}</div>
                <div className="italic">{draft.subhead}</div>
                <p className="mt-2 text-sm">{draft.body}</p>
                <div className="mt-2 font-semibold text-primary">{draft.callToAction}</div>
                <div className="mt-2 flex flex-wrap justify-center gap-2">
                  <button onClick={useDraft} className="min-h-11 bg-[hsl(var(--brand-blue))] px-4 text-sm font-semibold text-primary-foreground">Use these words</button>
                  <button onClick={() => void suggest()} className="min-h-11 border border-foreground px-4 text-sm font-semibold">Try another</button>
                </div>
              </div>
            )}
          </div>

          <div>
            <h3 className={h3}>3. Logo, photos and graphics</h3>
            <label className="mt-2 flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={onSite} onChange={(e) => setOnSite(e.target.checked)} className="h-5 w-5" />My logo and pictures are on my website. Please take them from there.</label>
            {onSite && <><label htmlFor="ah-site" className="sr-only">Your website</label><input id="ah-site" value={site} onChange={(e) => setSite(e.target.value)} placeholder="yourbusiness.com" className="mt-1 h-11 w-full border border-input bg-background px-3 text-sm sm:w-80" /></>}
            <label className="mt-3 inline-flex min-h-11 cursor-pointer items-center gap-2 border border-foreground px-4 text-sm font-semibold">
              <Upload className="h-4 w-4" />Upload graphics
              <input type="file" multiple accept="image/*,.pdf,.ai,.eps,.svg" className="sr-only" onChange={(e) => onGraphics([...graphics, ...Array.from(e.target.files ?? [])].slice(0, 10))} />
            </label>
            {graphics.length > 0 && <ul className="mt-2 space-y-1 text-sm">{graphics.map((g, i) => <li key={`${g.name}-${g.size}-${g.lastModified}`} className="flex items-center gap-2">{g.name}<button type="button" aria-label={`Remove ${g.name}`} onClick={() => onGraphics(graphics.filter((_, j) => j !== i))} className="flex h-9 w-9 items-center justify-center"><X className="h-4 w-4" /></button></li>)}</ul>}
          </div>
        </div>
      )}
    </section>
  );
}
